import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { type EmailSenderVerificationMethod } from "~/gql/graphqlBase.generated";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import { useSnacks } from "~/providers/SnackProvider";
import {
	useAddTenantEmailSenderMutation,
	useCompleteTenantEmailSenderVerificationMutation,
	useRefreshTenantEmailSenderStatusMutation,
	useRemoveTenantEmailSenderMutation,
	useRequestTenantEmailSenderVerificationMutation,
	useSetEmailSenderAdminOnlyMutation,
	useSetTenantDefaultEmailSenderMutation
} from "~/services/endpoints/apiEndpoints";

const SENDER_LABEL = "sender";
const REQUEST_VERIFICATION_ACTION = "requesting verification";

export interface AddEmailSenderInput {
	addressLine1: string;
	addressLine2?: string;
	city: string;
	country: string;
	displayName: string;
	email: string;
	stateProvince?: string;
	verificationMethod: EmailSenderVerificationMethod;
	zip?: string;
}

export interface AddedEmailSender {
	senderId: null | string;
}

export interface UseEmailSenderMutationsReturn {
	addEmailSender: (input: AddEmailSenderInput) => Promise<AddedEmailSender | null>;
	completeSenderVerification: (senderId: string, verificationLink: string) => Promise<boolean>;
	isAdding: boolean;
	isRefreshing: boolean;
	isSettingAdminOnly: boolean;
	isVerifying: boolean;
	refreshSenderStatus: (senderId: string) => Promise<boolean>;
	removeSender: (senderId: string) => Promise<boolean>;
	requestSenderVerification: (senderId: string) => Promise<boolean>;
	setDefaultSender: (senderId: string) => Promise<boolean>;
	setSenderAdminOnly: (senderId: string, adminOnly: boolean) => Promise<boolean>;
}

const useEmailSenderMutations = (): UseEmailSenderMutationsReturn => {
	const [openSnack] = useSnacks();
	const { t } = useTranslation("common");
	const { run, runForEntity, runForResult } = useMutationRunner();
	const [addMutation, { isLoading: isAdding }] = useAddTenantEmailSenderMutation();
	const [requestVerificationMutation] = useRequestTenantEmailSenderVerificationMutation();
	const [refreshMutation, { isLoading: isRefreshing }] = useRefreshTenantEmailSenderStatusMutation();
	const [setDefaultMutation] = useSetTenantDefaultEmailSenderMutation();
	const [setAdminOnlyMutation, { isLoading: isSettingAdminOnly }] = useSetEmailSenderAdminOnlyMutation();
	const [completeMutation, { isLoading: isVerifying }] = useCompleteTenantEmailSenderVerificationMutation();
	const [removeMutation] = useRemoveTenantEmailSenderMutation();

	const addEmailSender = useCallback(
		async (input: AddEmailSenderInput): Promise<AddedEmailSender | null> => {
			const payload = await runForEntity(
				addMutation({ input }),
				(data) => data.addTenantEmailSender,
				(addPayload) => addPayload,
				snackMessages.addError(SENDER_LABEL)
			);
			if (!payload) return null;

			const senderId = payload.sender?.senderId ?? null;
			if (senderId) {
				const { error } = await runForResult(
					requestVerificationMutation({ input: { senderId } }),
					(data) => data.requestTenantEmailSenderVerification,
					snackMessages.actionError(REQUEST_VERIFICATION_ACTION),
					{ silent: true }
				);
				if (error) {
					openSnack(t("admin.email.senderSetup.verificationRequestFailed"), "warning");
					return { senderId };
				}
			}

			openSnack(snackMessages.addSuccess("Sender"), "success");
			return { senderId };
		},
		[addMutation, openSnack, requestVerificationMutation, runForEntity, runForResult, t]
	);

	const requestSenderVerification = useCallback(
		(senderId: string): Promise<boolean> =>
			run(
				requestVerificationMutation({ input: { senderId } }),
				(data) => data.requestTenantEmailSenderVerification,
				snackMessages.actionError(REQUEST_VERIFICATION_ACTION),
				{ successMessage: "Verification requested" }
			),
		[requestVerificationMutation, run]
	);

	const refreshSenderStatus = useCallback(
		(senderId: string): Promise<boolean> =>
			run(
				refreshMutation({ input: { senderId } }),
				(data) => data.refreshTenantEmailSenderStatus,
				snackMessages.actionError("refreshing status"),
				{ successMessage: "Status refreshed" }
			),
		[refreshMutation, run]
	);

	const setDefaultSender = useCallback(
		(senderId: string): Promise<boolean> =>
			run(
				setDefaultMutation({ input: { senderId } }),
				(data) => data.setTenantDefaultEmailSender,
				snackMessages.updateError("default sender"),
				{ successMessage: "Default sender updated" }
			),
		[run, setDefaultMutation]
	);

	const setSenderAdminOnly = useCallback(
		(senderId: string, adminOnly: boolean): Promise<boolean> =>
			run(
				setAdminOnlyMutation({ input: { adminOnly, senderId } }),
				(data) => data.setEmailSenderAdminOnly,
				snackMessages.updateError(SENDER_LABEL),
				{
					successMessage: adminOnly ? "Sender restricted to admins" : "Sender opened to all users"
				}
			),
		[run, setAdminOnlyMutation]
	);

	const completeSenderVerification = useCallback(
		(senderId: string, verificationLink: string): Promise<boolean> =>
			run(
				completeMutation({ input: { senderId, verificationLink } }),
				(data) => data.completeTenantEmailSenderVerification,
				snackMessages.actionError("verifying sender"),
				{ successMessage: "Sender verified" }
			),
		[completeMutation, run]
	);

	const removeSender = useCallback(
		(senderId: string): Promise<boolean> =>
			run(
				removeMutation({ input: { senderId } }),
				(data) => data.removeTenantEmailSender,
				t("admin.email.senders.removeUnconfirmed"),
				{ successMessage: snackMessages.removeSuccess("Sender") }
			),
		[removeMutation, run, t]
	);

	return {
		addEmailSender,
		completeSenderVerification,
		isAdding,
		isRefreshing,
		isSettingAdminOnly,
		isVerifying,
		refreshSenderStatus,
		removeSender,
		requestSenderVerification,
		setDefaultSender,
		setSenderAdminOnly
	};
};

export default useEmailSenderMutations;
